// Pure derivation of the mode banner's appearance from the live-chat availability +
// handoff state. Extracted from ChatInterface so the state-machine → label/colour
// mapping (Miles 7/15 reskin) is unit-testable without mounting the whole chat widget.
//
// The banner is a reskin of the TRAC-732 pill: idle+open → red "Chat with a Tire
// Expert"; requested/connecting/queued/active → darker-red leave/cancel; outside LP
// hours → greyed but still clickable (opens the hours modal).

import { HUMAN_CONTACT } from '../../constants/humanContact';

export type HandoffState = 'idle' | 'connecting' | 'queued' | 'active' | 'ended';

export interface BannerInputs {
  handoffState: HandoffState;
  /** liveChatAvail.state — 'checking' | 'open' | anything-else (closed/unknown). */
  availState: string;
  /** routingPending — customer asked for a person but hasn't been routed yet. */
  routingPending: boolean;
}

export interface BannerModel {
  label: string;
  /** Drives the background colour class: brand red / darker leave-red / greyed. */
  variant: 'ai' | 'leave' | 'closed';
  /** true → render the ✕ icon (every leave/cancel action); false → the headset. */
  leaving: boolean;
  /** true → show the estimated-wait caption under the banner. */
  showWait: boolean;
  /** true → button is disabled (availability still being checked). */
  disabled: boolean;
}

export function bannerModel({ handoffState, availState, routingPending }: BannerInputs): BannerModel {
  const idle = handoffState === 'idle' || handoffState === 'ended';
  const checking = availState === 'checking';
  // Outside LP hours (or still checking): greyed. Still clickable — opens the hours modal.
  const closed = idle && availState !== 'open';
  // Requested but not yet connecting — an escape hatch: a second click cancels.
  const requested = idle && routingPending;
  // Every "get me out of here" action wears the darker red; only entering is brand red.
  const leaving = requested || !idle;

  const label = !idle
    ? (handoffState === 'active' ? 'Leave Live Chat' : 'Leave Queue')
    : checking
      ? 'Checking availability…'
      : requested
        ? 'Cancel request'
        : availState === 'open'
          ? 'Connect with a Tire Expert'
          : 'Live Chat Unavailable';

  return {
    label,
    variant: closed ? 'closed' : leaving ? 'leave' : 'ai',
    leaving,
    showWait: idle && availState === 'open' && !requested,
    disabled: checking,
  };
}

export interface HoursModalCopy {
  title: string;
  body: string;
  /** true → list the weekly live-chat hours under the body (closed-hours case only). */
  showSchedule: boolean;
}

/**
 * TRACI-425: the copy of the modal the greyed banner opens. That banner means one of
 * two things: outside live-chat hours, or a fresh decline because every rep is busy
 * (`closed_no_agents`, set by the decline handler). Telling a customer in open hours
 * that we are "outside live-chat hours" contradicts the busy message right under it.
 */
export function hoursModalCopy(availState: string, hours: string | null): HoursModalCopy {
  if (availState === 'closed_no_agents') {
    const { phone, extensions } = HUMAN_CONTACT;
    return {
      title: 'Every Tire Expert is busy right now',
      body: `Try again in a few minutes, or call ${phone} (Sales ext. ${extensions.sales}, ` +
        `Customer Care ext. ${extensions.customerCare}). The AI tire agent can keep helping you meanwhile.`,
      showSchedule: false,
    };
  }
  return {
    title: 'Live chat is closed right now',
    body: hours
      ? `We reopen ${hours}. The AI tire agent can keep helping you until then.`
      : `We're currently outside live-chat hours. The AI tire agent can keep helping you until we reopen.`,
    showSchedule: true,
  };
}
